import {pillowLayout,PILLOW_OUTLINE} from './pillow-layout';
import {useState} from 'react'
import {OCCUPANTS, type Occupant} from './occupants'
import type {Capacity} from './layout-summary'
const INK='#655239', GOLD='#a58132', GOLD_FILL='#c5a253', WOOD='#efe6d4', MUTED='#b9ad97'

const HEIGHT: Record<Occupant, number> = {
  small_children: 40,
  pre_teens: 56,
  teens: 66,
  adult_children: 70,
  adults: 69,
  grandparents: 65,
}

export function heightLabel(inches: number) {
  return `${Math.floor(inches / 12)}′ ${inches % 12}″`
}

export function mattressSpace(height: number, length: number) {
  return length >= height ? `${length - height}″ of length remaining` : `${height - length}″ longer than the mattress`
}

export const SHOULDER_PROFILES = [
  { id: 'young', groups: ['small_children'], label: 'Young children · 4–7', min: 11.5, max: 13 },
  { id: 'children', groups: ['pre_teens'], label: 'Children · 8–10', min: 13, max: 14 },
  { id: 'preteens', groups: ['pre_teens'], label: 'Preteens · 11–12', min: 14, max: 15 },
  { id: 'teen-girls', groups: ['teens'], label: 'Teens (girls) · 13–17', min: 15.5, max: 17 },
  { id: 'teen-boys', groups: ['teens'], label: 'Teens (boys) · 13–17', min: 16, max: 19 },
  { id: 'women', groups: ['adult_children', 'adults'], label: 'Adult women · 18–64', min: 17, max: 17 },
  { id: 'men', groups: ['adult_children', 'adults'], label: 'Adult men · 18–64', min: 19.5, max: 19.5 },
  { id: 'older-women', groups: ['grandparents'], label: 'Women · 65+', min: 16, max: 17 },
  { id: 'older-men', groups: ['grandparents'], label: 'Men · 65+', min: 18.5, max: 19.5 },
]

export const COMPARISON_MATTRESSES = [
  { id: 'twin', label: 'Twin', width: 38, length: 75 },
  { id: 'twin-xl', label: 'Twin XL', width: 38, length: 80 },
  { id: 'full', label: 'Full', width: 54, length: 75 },
  { id: 'queen', label: 'Queen', width: 60, length: 80 },
]

/** Overhead outline: height and outer shoulder breadth are independent dimensions. */
function OverheadSleeper({ height, shoulders }: { height: number; shoulders: number }) {
  const h = height * 2.6, w = shoulders * 2.6
  return <g fill={GOLD_FILL} stroke="#806324" strokeWidth="1">
    <ellipse cx={h * .06} cy="0" rx={h * .06} ry={w * .18} />
    <path d={`M${h * .145} ${-w * .19} Q${h * .15} ${-w / 2} ${h * .21} ${-w / 2}
      L${h * .43} ${-w * .43} L${h * .48} ${-w * .32} L${h * .56} ${-w * .36}
      L${h} ${-w * .23} L${h} ${-w * .07} L${h * .59} 0
      L${h} ${w * .07} L${h} ${w * .23} L${h * .56} ${w * .36}
      L${h * .48} ${w * .32} L${h * .43} ${w * .43} L${h * .21} ${w / 2}
      Q${h * .15} ${w / 2} ${h * .145} ${w * .19} Z`} />
  </g>
}

export function OccupantsVisual({ ids, value, onChange, initialMattressIds = ['twin', 'twin-xl'] }: { ids: Occupant[]; value: Capacity; onChange?: (value: Capacity) => void; initialMattressIds?: string[] }) {
  const [active, setActive] = useState<Occupant>(ids.find(id => value.occupants?.includes(id)) ?? ids[0])
  const [lying, setLying] = useState(false)
  const [sleeperCounts, setSleeperCounts] = useState([1, 1])
  const [mattressIds, setMattressIds] = useState(initialMattressIds)
  const [profileIds, setProfileIds] = useState<Partial<Record<Occupant, string>>>({})
  const profiles = SHOULDER_PROFILES.filter(p => p.groups.includes(active))
  const profile = profiles.find(p => p.id === profileIds[active]) ?? profiles[0]
  const shoulders = (profile.min + profile.max) / 2
  const height = Math.max(30, Math.min(84, value.heightInches?.[active] ?? HEIGHT[active]))
  const label = OCCUPANTS.find(o => o.id === active)!.label
  return (
    <div className="height-comparison">
      <div className="height-groups" role="group" aria-label="Choose a group to compare">
        {ids.map(id => <button type="button" key={id} aria-pressed={active === id} onClick={() => setActive(id)}>
          {OCCUPANTS.find(o => o.id === id)!.label}
        </button>)}
      </div>
      <label className="height-slider">
        <span>{label} · height <strong>{heightLabel(height)} ({height}″)</strong></span>
        <input type="range" min="30" max="84" step="1" value={height} aria-label={`${label} height`} aria-valuetext={heightLabel(height)}
          onChange={e => onChange?.({ ...value, heightInches: { ...value.heightInches, [active]: Number(e.target.value) } })} />
        <small>Example starting heights — slide to match your sleepers.</small>
      </label>
      <label className="shoulder-profile">
        <span>Shoulder-width example</span>
        <select value={profile.id} onChange={e => setProfileIds({ ...profileIds, [active]: e.target.value })}>
          {profiles.map(p => <option value={p.id} key={p.id}>{p.label}</option>)}
        </select>
        <small>Outer shoulder width: {profile.min === profile.max ? `${profile.min}″` : `${profile.min}–${profile.max}″`}. Drawing uses {shoulders}″{profile.min !== profile.max ? ' (the midpoint)' : ''}.</small>
      </label>
      <button className="height-animate" type="button" onClick={() => setLying(!lying)}>
        {lying ? 'Stand back up ↗' : 'Lie down & compare →'}
      </button>
      <div className="height-mattresses">
        {mattressIds.map((id, index) => {
          const { label: mattressLabel, width, length } = COMPARISON_MATTRESSES.find(m => m.id === id)!
          const count = sleeperCounts[index]
          const widthLeft = width - shoulders * count
          return <div className="height-mattress" key={index}>
            <label className="comparison-select">{index === 0 ? 'First mattress' : 'Second mattress'}
              <select value={id} onChange={e => setMattressIds(mattressIds.map((m, i) => i === index ? e.target.value : m))}>
                {COMPARISON_MATTRESSES.map(m => <option value={m.id} key={m.id}>{m.label} · {m.width}″ × {m.length}″</option>)}
              </select>
            </label>
            <label className="comparison-select">Sleepers across {mattressLabel}
              <select value={count} onChange={e => { setSleeperCounts(sleeperCounts.map((n, i) => i === index ? Number(e.target.value) : n)); setLying(true) }}>
                {[1, 2, 3].map(n => <option value={n} key={n}>{n} {n === 1 ? 'sleeper' : 'sleepers'}</option>)}
              </select>
            </label>
            <svg viewBox="0 0 360 290" role="img" aria-label={`Top view: ${profile.label}, ${heightLabel(height)} tall and ${shoulders} inches across shoulders, ${count} sleeper${count === 1 ? '' : 's'} across a ${width} by ${length}-inch mattress: ${mattressSpace(height, length)}`}>
              <rect x="28" y={145 - width * 2.6 / 2} width={length * 2.6} height={width * 2.6} rx="7" fill={WOOD} stroke={INK} />
              {pillowLayout(width).offsets.map((offset,i)=><path key={i} d={PILLOW_OUTLINE} transform={`translate(32 ${145+(offset-pillowLayout(width).width/2)*2.6}) scale(${pillowLayout(width).depth*2.6/20} ${pillowLayout(width).width*2.6/26})`} fill="#fffdf9" stroke={MUTED} strokeWidth=".65"/>)}
              <text x={28 + length * 2.6 / 2} y="55" textAnchor="middle" className="gv-sub" fill={INK}>Top view · {length}″ long</text>
              <path d={`M21 ${145 - width * 2.6 / 2}h-6v${width * 2.6}h6`} fill="none" stroke={GOLD} />
              <text x={28 + length * 2.6 / 2} y="258" textAnchor="middle" className="gv-sub" fill={INK}>{width}″ mattress width</text>
              {Array.from({ length: count }, (_, sleeper) => {
                const cy = 145 - width * 2.6 / 2 + (sleeper + .5) * width * 2.6 / count
                return <g key={sleeper} className="height-fly" style={{ transform: lying ? `translate(28px, ${cy}px) rotate(0deg)` : `translate(${300 + sleeper * 6}px, ${270 - height * 2.6}px) rotate(90deg)` }}>
                  <OverheadSleeper height={height} shoulders={shoulders} />
                </g>
              })}
              {lying && <line x1={28 + height * 2.6} x2={28 + length * 2.6} y1="145" y2="145" stroke={height > length ? '#a54533' : GOLD} strokeWidth="3" strokeDasharray="4 3" />}

            </svg>
            <span className={height > length ? 'height-overflow' : ''} aria-live="polite">{mattressSpace(height, length)}</span>
            <small>{Math.round(height / length * 100)}% of mattress length</small>
            <small>{count} × {shoulders}″ shoulders = {shoulders * count}″ across</small>
            <small className={widthLeft < 0 ? 'shoulder-overflow' : ''}>{widthLeft >= 0 ? `${widthLeft}″ width left in total` : `${-widthLeft}″ more shoulder width than the mattress`}</small>
          </div>
        })}
      </div>
      <div className="height-pro-tip">
        <p><strong>Pro tip:</strong> Choose a mattress 6–12″ longer than the sleeper’s height to leave room for feet to extend during sleep.</p>
        <p aria-live="polite">For a {heightLabel(height)} sleeper, aim for <strong>{height + 6}–{height + 12}″ of mattress length.</strong></p>
      </div>
      <p className="height-note">Both views use the same scale. Shoulder examples use outer shoulder width, including the deltoids, rather than bone-to-bone width. Each figure uses the selected profile. Shoulder fit alone does not establish comfortable sleeping capacity: allow room for arms, movement and bedding. Individual bodies and sleeping positions vary. Your bed selection stays yours.</p>
    </div>
  )
}

